import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaBriefcase, 
  FaSearch, 
  FaMapMarkerAlt, 
  FaClock, 
  FaBuilding, 
  FaTrash, 
  FaEdit, 
  FaPlus,
  FaEye,
  FaLock,
  FaUnlock,
  FaFilter
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, getDoc, query, where } from 'firebase/firestore';
import JobApplication from './JobApplication';
import SEO from '../SEO';
import './JobBoard.css';

const JobBoard = () => {
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [userApplications, setUserApplications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplication, setShowApplication] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [newJob, setNewJob] = useState({
    title: '',
    company: '',
    location: '',
    type: 'Full-time',
    description: '',
    requirements: [],
    status: 'Open',
    deadline: '',
    postedDate: new Date().toISOString().split('T')[0]
  });
  const [newRequirement, setNewRequirement] = useState('');

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists() && userSnap.data().role === 'admin') {
            setIsAdmin(true);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
        }
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  useEffect(() => {
    fetchJobs();
    if (currentUser) {
      fetchApplications();
    }
  }, [currentUser]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const jobsData = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(jobsData);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch jobs. Please try again later.');
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', currentUser.uid)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applicationsData = applicationsSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setUserApplications(applicationsData);

      // If admin, fetch all applications
      if (isAdmin) {
        const allApplicationsSnapshot = await getDocs(collection(db, 'applications'));
        const allApplicationsData = allApplicationsSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setApplications(allApplicationsData);
      }
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleTypeChange = (e) => setSelectedType(e.target.value);
  const handleLocationChange = (e) => setSelectedLocation(e.target.value);

  // User applying for job
  const handleApply = (job) => {
    setSelectedJob(job);
    setShowApplication(true);
  };

  // Submitting an application
  const handleApplicationSubmit = async (applicationData) => {
    try {
      // Create application in Firestore
      await addDoc(collection(db, 'applications'), {
        jobId: selectedJob.id,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        userEmail: currentUser.email,
        ...applicationData,
        status: 'Pending',
        appliedDate: new Date().toISOString()
      });
      
      // Update local state
      const newApplication = {
        id: Date.now().toString(),
        jobId: selectedJob.id,
        userId: currentUser.uid,
        ...applicationData,
        status: 'Pending',
        appliedDate: new Date().toISOString()
      };
      
      setUserApplications([...userApplications, newApplication]);
      setShowApplication(false);
      
      // Show success message
      alert('Application submitted successfully!');
    } catch (err) {
      setError('Failed to submit application. Please try again.');
      console.error(err);
    }
  };

  // Admin job post/edit form handler
  const handleJobFormChange = (e) => {
    const { name, value } = e.target;
    setNewJob(prev => ({ ...prev, [name]: value }));
  };

  const handleAddRequirement = () => {
    if (newRequirement.trim()) {
      setNewJob(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const handleRemoveRequirement = (index) => {
    setNewJob(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  // Admin creating or updating a job
  const handleSaveJob = async () => {
    try {
      if (editingJob) {
        // Update existing job
        await updateDoc(doc(db, 'jobs', editingJob.id), newJob);
        setJobs(jobs.map(job => job.id === editingJob.id ? { ...newJob, id: editingJob.id } : job));
      } else {
        // Create new job
        const docRef = await addDoc(collection(db, 'jobs'), {
          ...newJob,
          postedDate: new Date().toISOString()
        });
        setJobs([...jobs, { id: docRef.id, ...newJob }]);
      }
      setShowJobForm(false);
      setEditingJob(null);
      setNewJob({
        title: '',
        company: '',
        location: '',
        type: 'Full-time',
        description: '',
        requirements: [],
        status: 'Open',
        deadline: '',
        postedDate: new Date().toISOString().split('T')[0]
      });
    } catch (err) {
      console.error('Error saving job:', err);
      setError('Failed to save job. Please try again.');
    }
  };

  // Admin editing a job
  const handleEditJob = (job) => {
    setEditingJob(job);
    setNewJob({
      ...job,
      deadline: job.deadline ? job.deadline.split('T')[0] : '',
      postedDate: job.postedDate ? job.postedDate.split('T')[0] : ''
    });
    setShowJobForm(true);
  };

  // Admin deleting a job
  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteDoc(doc(db, 'jobs', jobId));
        setJobs(jobs.filter(job => job.id !== jobId));
      } catch (err) {
        console.error('Error deleting job:', err);
        setError('Failed to delete job. Please try again.');
      }
    }
  };

  // Admin toggling job status (open/closed)
  const handleToggleJobStatus = async (jobId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Open' ? 'Closed' : 'Open';
      await updateDoc(doc(db, 'jobs', jobId), { status: newStatus });
      setJobs(jobs.map(job => job.id === jobId ? { ...job, status: newStatus } : job));
    } catch (err) {
      console.error('Error updating job status:', err);
      setError('Failed to update job status. Please try again.');
    }
  };

  // Admin viewing applications for a specific job
  const handleViewApplications = (jobId) => {
    // Filter applications for this job
    const jobApplications = applications.filter(app => app.jobId === jobId);
    
    // Display them in a modal or redirect to applications page
    console.log('Applications for job', jobId, jobApplications);
    // TODO: Implement modal or redirect to show applications
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || job.type === selectedType;
    const matchesLocation = selectedLocation === 'all' || job.location === selectedLocation;
    return matchesSearch && matchesType && matchesLocation;
  });

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <>
      <SEO 
        title="Job Board - Student Helper"
        description="Find job opportunities and internships for students."
        keywords="student jobs, internships, career opportunities, student employment"
      />
      <div className="job-board">
        <div className="header">
          <h1>Job Board</h1>
          <p>Find opportunities that match your skills and interests</p>
          
          {isAdmin && (
            <button className="add-job-btn" onClick={() => {
              setEditingJob(null);
              setNewJob({
                title: '',
                company: '',
                location: '',
                type: 'Full-time',
                description: '',
                requirements: [],
                status: 'Open',
                deadline: '',
                postedDate: new Date().toISOString().split('T')[0]
              });
              setShowJobForm(true);
            }}>
              <FaPlus /> Post New Job
            </button>
          )}
        </div>

        <div className="search-filters">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          <div className="filters">
            <FaFilter className="filter-icon" />
            <select value={selectedType} onChange={handleTypeChange}>
              <option value="all">All Types</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Internship">Internship</option>
              <option value="Contract">Contract</option>
            </select>
          </div>
          <div className="filters">
            <FaMapMarkerAlt className="filter-icon" />
            <select value={selectedLocation} onChange={handleLocationChange}>
              <option value="all">All Locations</option>
              <option value="New York">New York</option>
              <option value="Remote">Remote</option>
              <option value="Los Angeles">Los Angeles</option>
              <option value="Chicago">Chicago</option>
              <option value="San Francisco">San Francisco</option>
            </select>
          </div>
        </div>

        <div className="jobs-grid">
          {filteredJobs.length > 0 ? filteredJobs.map(job => (
            <motion.div
              key={job.id}
              className="job-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="job-header">
                <h3>{job.title}</h3>
                <span className={`status ${job.status?.toLowerCase()}`}>
                  {job.status}
                </span>
              </div>
              <div className="job-company">
                <FaBuilding className="job-company-icon" /> {job.company}
              </div>
              <div className="job-details">
                <span className="job-tag"><FaMapMarkerAlt /> {job.location}</span>
                <span className="job-tag"><FaBriefcase /> {job.type}</span>
                {job.deadline && (
                  <div className="job-deadline"><FaClock /> Deadline: {new Date(job.deadline).toLocaleDateString()}</div>
                )}
              </div>
              <p className="job-description">{job.description}</p>
              {job.requirements?.length > 0 && (
                <div className="job-requirements">
                  <h4>Requirements:</h4>
                  <ul>
                    {job.requirements?.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {isAdmin ? (
                <div className="admin-actions">
                  <button 
                    className="admin-btn edit" 
                    onClick={() => handleEditJob(job)}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button 
                    className="admin-btn delete" 
                    onClick={() => handleDeleteJob(job.id)}
                  >
                    <FaTrash /> Delete
                  </button>
                  <button 
                    className="admin-btn toggle-status" 
                    onClick={() => handleToggleJobStatus(job.id, job.status)}
                  >
                    {job.status === 'Open' ? <FaLock /> : <FaUnlock />} 
                    {job.status === 'Open' ? 'Close' : 'Open'}
                  </button>
                  <button 
                    className="admin-btn view-apps" 
                    onClick={() => handleViewApplications(job.id)}
                  >
                    <FaEye /> Applications
                  </button>
                </div>
              ) : (
                <button
                  className="apply-btn"
                  onClick={() => handleApply(job)}
                  disabled={!currentUser || job.status !== 'Open'}
                >
                  {!currentUser
                    ? 'Login to Apply'
                    : job.status !== 'Open'
                    ? 'Closed'
                    : userApplications.some(app => app.jobId === job.id)
                    ? 'Applied'
                    : 'Apply Now'}
                </button>
              )}
            </motion.div>
          )) : (
            <div className="no-jobs">
              <FaBriefcase className="no-jobs-icon" />
              <h2>No Jobs Available</h2>
              <p>Check back later for new opportunities or try adjusting your search</p>
            </div>
          )}
        </div>
      </div>

      {showApplication && (
        <JobApplication
          job={selectedJob}
          onSubmit={handleApplicationSubmit}
          onClose={() => setShowApplication(false)}
        />
      )}

      {showJobForm && (
        <div className="job-form-overlay">
          <div className="job-form-modal">
            <h2>{editingJob ? 'Edit Job' : 'Post New Job'}</h2>
            <button 
              className="close-form-btn" 
              onClick={() => {
                setShowJobForm(false);
                setEditingJob(null);
              }}
            >
              <FaTrash />
            </button>

            <div className="job-form">
              <div className="form-group">
                <label>Job Title*</label>
                <input
                  type="text"
                  name="title"
                  value={newJob.title}
                  onChange={handleJobFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Company*</label>
                <input
                  type="text"
                  name="company"
                  value={newJob.company}
                  onChange={handleJobFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Location*</label>
                <input
                  type="text"
                  name="location"
                  value={newJob.location}
                  onChange={handleJobFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Job Type*</label>
                <select
                  name="type"
                  value={newJob.type}
                  onChange={handleJobFormChange}
                  required
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Internship">Internship</option>
                  <option value="Contract">Contract</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description*</label>
                <textarea
                  name="description"
                  value={newJob.description}
                  onChange={handleJobFormChange}
                  rows="4"
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Requirements</label>
                <div className="requirements-input">
                  <input
                    type="text"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    placeholder="Add a requirement"
                  />
                  <button 
                    type="button" 
                    onClick={handleAddRequirement}
                    className="add-req-btn"
                  >
                    Add
                  </button>
                </div>
                <ul className="requirements-list">
                  {newJob.requirements.map((req, index) => (
                    <li key={index}>
                      {req}
                      <button 
                        type="button" 
                        onClick={() => handleRemoveRequirement(index)}
                        className="remove-req-btn"
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="form-group">
                <label>Deadline</label>
                <input
                  type="date"
                  name="deadline"
                  value={newJob.deadline}
                  onChange={handleJobFormChange}
                />
              </div>

              <div className="form-group">
                <label>Status*</label>
                <select
                  name="status"
                  value={newJob.status}
                  onChange={handleJobFormChange}
                  required
                >
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>

              <button 
                type="button" 
                className="save-job-btn"
                onClick={handleSaveJob}
              >
                {editingJob ? 'Update Job' : 'Post Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JobBoard; 